/*
 * CAREERS INTERACTION STORYBOARD
 *
 * Page entry
 *   0ms  the hero begins resolving immediately from a soft rise and blur
 * 160ms  the two-part introduction settles into place
 * 320ms  the open-roles area enters as the final, heavier page region
 *
 * Role disclosure
 *   0ms  the selected row acknowledges the action and its content track begins opening
 * 240ms  the content settles from -6px while the disclosure indicator completes its turn
 *   0ms  closing starts immediately from the current rendered state
 * 180ms  the content is fully collapsed, then the native details element closes
 *
 * Switching roles closes the active role before opening the next, so only one is open.
 * Keyboard and reduced-motion interactions update immediately without transition latency.
 */

const CAREERS_ENTRY_SELECTOR = '[data-careers-entry]';
const CAREERS_ENTRY_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const CAREERS_ENTRY_TRANSITIONS = Object.freeze({
  hero: {
    delay: 0,
    duration: 680,
    keyframes: [
      { opacity: 0, transform: 'translateY(18%)', filter: 'blur(7px)' },
      { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
    ],
  },
  intro: {
    delay: 160,
    duration: 680,
    keyframes: [
      { opacity: 0, transform: 'translateY(12px)', filter: 'blur(6px)' },
      { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
    ],
  },
  roles: {
    delay: 320,
    duration: 720,
    keyframes: [
      { opacity: 0, transform: 'translate3d(0, 16px, 0) scale(0.992)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
    ],
  },
});

const ROLE_TRANSITION = Object.freeze({
  openDuration: 240,
  closeDuration: 180,
  propertyName: 'grid-template-rows',
});

const roleTransitions = new WeakMap();

const completeCareersEntry = (candidates = []) => {
  const pageRoot = document.documentElement;

  pageRoot.dataset.careersEntryState = 'complete';
  pageRoot.removeAttribute('data-careers-entry-arrival');
  candidates.forEach((element) => element.classList.remove('is-careers-entry-active'));
};

const intersectsInitialViewport = (element, viewportHeight, headerBottom) => {
  const bounds = element.getBoundingClientRect();

  return (
    bounds.width > 0 &&
    bounds.height > 0 &&
    bounds.right > 0 &&
    bounds.left < window.innerWidth &&
    bounds.bottom > headerBottom &&
    bounds.top < viewportHeight
  );
};

export const initialiseCareersEntry = (careers) => {
  const pageRoot = document.documentElement;
  const candidates = [...careers.querySelectorAll(CAREERS_ENTRY_SELECTOR)];

  if (
    window.matchMedia(REDUCED_MOTION_QUERY).matches ||
    typeof Element.prototype.animate !== 'function' ||
    pageRoot.dataset.careersEntryStarted === 'true'
  ) {
    completeCareersEntry(candidates);
    return;
  }

  pageRoot.dataset.careersEntryStarted = 'true';

  window.requestAnimationFrame(() => {
    const viewportHeight = window.innerHeight;
    const header = document.querySelector('.site-header');
    const headerBottom = header instanceof HTMLElement
      ? Math.max(0, header.getBoundingClientRect().bottom)
      : 0;
    const visibleTargets = candidates.filter((element) =>
      intersectsInitialViewport(element, viewportHeight, headerBottom),
    );
    const animations = [];

    visibleTargets.forEach((element) => {
      const transition = CAREERS_ENTRY_TRANSITIONS[element.dataset.careersEntry];

      if (!transition) {
        return;
      }

      element.classList.add('is-careers-entry-active');
      animations.push(
        element.animate(transition.keyframes, {
          delay: transition.delay,
          duration: transition.duration,
          easing: CAREERS_ENTRY_EASING,
          fill: 'both',
        }),
      );
    });

    pageRoot.dataset.careersEntryState = 'running';

    if (!animations.length) {
      completeCareersEntry(candidates);
      return;
    }

    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      completeCareersEntry(candidates);
      animations.forEach((animation) => animation.cancel());
    });
  });
};

const settleRole = (role, open) => {
  role.dataset.careersRoleState = open ? 'open' : 'closed';
  role.open = open;
};

const cancelRoleTransition = (role) => {
  roleTransitions.get(role)?.cancel();
};

const waitForRoleTransition = (role, open) => {
  const motion = role.querySelector(':scope > [data-careers-role-motion]');
  const duration = open ? ROLE_TRANSITION.openDuration : ROLE_TRANSITION.closeDuration;

  if (!(motion instanceof HTMLElement)) {
    settleRole(role, open);
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let settled = false;
    let timer = 0;

    const cleanup = () => {
      window.clearTimeout(timer);
      motion.removeEventListener('transitionend', handleTransitionEnd);
    };

    const finish = (completed) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();

      if (roleTransitions.get(role)?.cancel === cancel) {
        roleTransitions.delete(role);
      }

      if (completed) {
        settleRole(role, open);
      }

      resolve(completed);
    };

    const cancel = () => finish(false);

    function handleTransitionEnd(event) {
      if (event.target === motion && event.propertyName === ROLE_TRANSITION.propertyName) {
        finish(true);
      }
    }

    roleTransitions.set(role, { cancel });
    motion.addEventListener('transitionend', handleTransitionEnd);
    timer = window.setTimeout(() => finish(true), duration + 80);
  });
};

const transitionRole = (role, open, instant) => {
  const motion = role.querySelector(':scope > [data-careers-role-motion]');

  cancelRoleTransition(role);

  if (instant || !(motion instanceof HTMLElement)) {
    settleRole(role, open);
    return Promise.resolve(true);
  }

  if (open) {
    const wasOpen = role.open;
    role.open = true;

    if (!wasOpen) {
      role.dataset.careersRoleState = 'closed';
      motion.getBoundingClientRect();
    }

    role.dataset.careersRoleState = 'opening';
  } else {
    if (!role.open && role.dataset.careersRoleState !== 'opening') {
      settleRole(role, false);
      return Promise.resolve(true);
    }

    role.open = true;
    role.dataset.careersRoleState = 'closing';
  }

  return waitForRoleTransition(role, open);
};

export const initialiseCareersRoles = (root) => {
  const roles = [...root.querySelectorAll('[data-careers-role]')].filter(
    (role) => role instanceof HTMLDetailsElement,
  );

  if (!roles.length) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let interaction = 0;

  roles.forEach((role) => {
    const summary = role.querySelector(':scope > summary');
    const motion = role.querySelector(':scope > [data-careers-role-motion]');

    if (!(summary instanceof HTMLElement) || !(motion instanceof HTMLElement)) {
      return;
    }

    if (role.hasAttribute('data-careers-role-ready')) return;

    role.dataset.careersRoleState = role.open ? 'open' : 'closed';
    role.setAttribute('data-careers-role-ready', '');

    summary.addEventListener('click', async (event) => {
      event.preventDefault();

      interaction += 1;
      const currentInteraction = interaction;
      const currentState = role.dataset.careersRoleState;
      const wantsOpen = currentState !== 'open' && currentState !== 'opening';
      const instant = reducedMotion.matches || event.detail === 0;

      if (!wantsOpen) {
        await transitionRole(role, false, instant);
        return;
      }

      const activeRoles = roles.filter(
        (candidate) => candidate !== role && candidate.open,
      );

      await Promise.all(
        activeRoles.map((candidate) => transitionRole(candidate, false, instant)),
      );

      if (currentInteraction !== interaction) {
        return;
      }

      await transitionRole(role, true, instant);
    });
  });
};

const initialiseCareers = (scope) => {
  scope.querySelectorAll('[data-careers-root]').forEach((root) => {
    initialiseCareersEntry(root);
    initialiseCareersRoles(root);
  });
};

if (typeof document !== 'undefined') {
  initialiseCareers(document);
  document.addEventListener('shopify:section:load', (event) => initialiseCareers(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const role = event.target.closest('[data-careers-role]');
    if (!(role instanceof HTMLDetailsElement)) return;
    role.closest('[data-careers-root]').querySelectorAll('[data-careers-role]').forEach((candidate) => {
      cancelRoleTransition(candidate);
      settleRole(candidate, candidate === role);
    });
  });
}
